import { useState, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { useModalAnimation } from '@/hooks/useModalAnimation';

interface EdicaoEmLoteModalProps {
  onClose: () => void;
  onSuccess: (count: number) => void;
}

type Campo = 'carreira' | 'nivel_atual' | 'data_entrada_funcao_publica' | 'unidade_organica';

interface EditRow {
  id: string;
  nome: string;
  carreira: string;
  nivel_atual: number;
  data_entrada: string;
  unidade_organica: string;
  selecionado: boolean;
  modificado: boolean;
}

const CARREIRAS = ['Técnica Superior', 'Assistente Técnico', 'Assistente Operacional', 'Dirigente', 'Informática'];

const UNIDADES = [
  'UGA - Unidade de Gestão e Administração',
  'DDHS - DIVISÃO DE DESENVOLVIMENTO HUMANO E SOCIAL',
  'Departamento de Recursos Humanos',
  'Serviços Gerais',
  'DPGU - Divisão de Planeamento e Gestão Urbanística',
  'DSU - Divisão de Serviços Urbanos',
  'Gabinete do Presidente',
  'Departamento Financeiro',
];

export default function EdicaoEmLoteModal({ onClose, onSuccess }: EdicaoEmLoteModalProps) {
  const { animating, handleClose } = useModalAnimation(onClose);
  const { funcionarios, reload: refreshFuncionarios } = useFuncionarios();
  const { activeMunicipio } = useActiveMunicipio();

  const [step, setStep] = useState<'filtro' | 'editar'>('filtro');
  const [filtroCarreira, setFiltroCarreira] = useState('');
  const [filtroSemDados, setFiltroSemDados] = useState(true);
  const [filtroSearch, setFiltroSearch] = useState('');

  const [rows, setRows] = useState<EditRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [campoGlobal, setCampoGlobal] = useState<Campo>('carreira');
  const [valorGlobal, setValorGlobal] = useState('');

  const funcionariosFiltrados = useMemo(() => {
    return funcionarios.filter((f) => {
      const matchCarreira = filtroCarreira === '' || f.carreira === filtroCarreira;
      const matchSearch = filtroSearch === '' || f.nome.toLowerCase().includes(filtroSearch.toLowerCase());
      const matchSemDados = !filtroSemDados || !f.dataEntradaFuncaoPublica || !f.unidadeOrganica;
      return matchCarreira && matchSearch && matchSemDados;
    });
  }, [funcionarios, filtroCarreira, filtroSearch, filtroSemDados]);

  function abrirEditar() {
    const editRows: EditRow[] = funcionariosFiltrados.map((f) => ({
      id: f.id,
      nome: f.nome,
      carreira: f.carreira,
      nivel_atual: f.nivelAtual || 1,
      data_entrada: f.dataEntradaFuncaoPublica || '',
      unidade_organica: f.unidadeOrganica || '',
      selecionado: true,
      modificado: false,
    }));
    setRows(editRows);
    setStep('editar');
  }

  function toggleSelecionado(id: string) {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, selecionado: !r.selecionado } : r));
  }

  function toggleTodos() {
    const allSelected = rows.every((r) => r.selecionado);
    setRows((prev) => prev.map((r) => ({ ...r, selecionado: !allSelected })));
  }

  function updateRow(id: string, field: keyof EditRow, value: string | number | boolean) {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value, modificado: true } : r));
  }

  function aplicarGlobal() {
    if (!valorGlobal) return;
    setRows((prev) => prev.map((r) => {
      if (!r.selecionado) return r;
      if (campoGlobal === 'nivel_atual') return { ...r, nivel_atual: Number(valorGlobal), modificado: true };
      if (campoGlobal === 'data_entrada_funcao_publica') return { ...r, data_entrada: valorGlobal, modificado: true };
      if (campoGlobal === 'unidade_organica') return { ...r, unidade_organica: valorGlobal, modificado: true };
      if (campoGlobal === 'carreira') return { ...r, carreira: valorGlobal, modificado: true };
      return r;
    }));
    setValorGlobal('');
  }

  async function guardar() {
    const selecionados = rows.filter((r) => r.selecionado && r.modificado);
    if (selecionados.length === 0) {
      setError('Nenhuma alteração para guardar.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      for (const row of selecionados) {
        const { error: err } = await supabase
          .from('funcionarios')
          .update({
            carreira: row.carreira,
            nivel_atual: row.nivel_atual,
            data_entrada_funcao_publica: row.data_entrada || null,
            unidade_organica: row.unidade_organica || null,
          })
          .eq('id', row.id)
          .eq('municipio_id', activeMunicipio?.id);
        if (err) throw err;
      }
      await refreshFuncionarios();
      onSuccess(selecionados.length);
    } catch (e: unknown) {
      setError('Erro ao guardar: ' + (e instanceof Error ? e.message : 'Erro desconhecido'));
    } finally {
      setSaving(false);
    }
  }

  const selecionadosCount = rows.filter((r) => r.selecionado).length;
  const modificadosCount = rows.filter((r) => r.selecionado && r.modificado).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className={`absolute inset-0 bg-black/40 ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} onClick={handleClose} />
      <div className={`relative bg-white rounded-2xl w-full max-w-5xl mx-4 flex flex-col max-h-[90vh] overflow-hidden ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-zinc-900 rounded-lg">
              <i className="ri-edit-2-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-zinc-900">Edição em Lote</h2>
              <p className="text-xs text-zinc-400">Corrigir carreira, nível, data de entrada e unidade orgânica</p>
            </div>
          </div>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {step === 'filtro' ? (
          <div className="p-6 space-y-5">
            <p className="text-sm text-zinc-600">Seleciona quais funcionários pretendes editar em lote:</p>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-1.5">Filtrar por carreira</label>
                <select
                  value={filtroCarreira}
                  onChange={(e) => setFiltroCarreira(e.target.value)}
                  className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
                >
                  <option value="">Todas as carreiras</option>
                  {CARREIRAS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-1.5">Pesquisar por nome</label>
                <input
                  type="text"
                  value={filtroSearch}
                  onChange={(e) => setFiltroSearch(e.target.value)}
                  placeholder="Nome do funcionário..."
                  className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300"
                />
              </div>
              <div className="flex flex-col justify-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filtroSemDados}
                    onChange={(e) => setFiltroSemDados(e.target.checked)}
                    className="w-4 h-4 rounded accent-zinc-800"
                  />
                  <span className="text-sm text-zinc-700">Apenas com campos em falta</span>
                </label>
              </div>
            </div>

            <div className="bg-zinc-50 rounded-xl border border-zinc-100 p-4">
              <p className="text-sm font-semibold text-zinc-700 mb-3">
                {funcionariosFiltrados.length} funcionário(s) correspondem aos filtros:
              </p>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {funcionariosFiltrados.slice(0, 20).map((f) => (
                  <div key={f.id} className="flex items-center justify-between text-xs py-1.5 px-3 bg-white rounded-lg border border-zinc-100">
                    <span className="font-medium text-zinc-800">{f.nome}</span>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${f.carreira === 'Técnica Superior' ? 'bg-zinc-100 text-zinc-600' : f.carreira === 'Assistente Técnico' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                        {f.carreira}
                      </span>
                      {!f.dataEntradaFuncaoPublica && <span className="text-[10px] text-red-400">Sem data entrada</span>}
                      {!f.unidadeOrganica && <span className="text-[10px] text-amber-500">Sem unidade</span>}
                    </div>
                  </div>
                ))}
                {funcionariosFiltrados.length > 20 && (
                  <p className="text-xs text-zinc-400 text-center pt-2">+ {funcionariosFiltrados.length - 20} mais...</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={handleClose} className="whitespace-nowrap px-4 py-2.5 text-sm text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer">
                Cancelar
              </button>
              <button
                onClick={abrirEditar}
                disabled={funcionariosFiltrados.length === 0}
                className="whitespace-nowrap px-5 py-2.5 text-sm font-semibold bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 transition-colors cursor-pointer"
              >
                Editar {funcionariosFiltrados.length} funcionário(s)
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Barra de ações globais */}
            <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
              <span className="text-xs text-zinc-500 font-medium">Aplicar a {selecionadosCount} selecionado(s):</span>
              <select
                value={campoGlobal}
                onChange={(e) => { setCampoGlobal(e.target.value as Campo); setValorGlobal(''); }}
                className="text-xs border border-zinc-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none cursor-pointer"
              >
                <option value="carreira">Carreira</option>
                <option value="nivel_atual">Nível Atual</option>
                <option value="data_entrada_funcao_publica">Data Entrada F.P.</option>
                <option value="unidade_organica">Unidade Orgânica</option>
              </select>

              {campoGlobal === 'carreira' && (
                <select
                  value={valorGlobal}
                  onChange={(e) => setValorGlobal(e.target.value)}
                  className="text-xs border border-zinc-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none cursor-pointer"
                >
                  <option value="">Escolher carreira...</option>
                  {CARREIRAS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
              {campoGlobal === 'nivel_atual' && (
                <input
                  type="number" min="1" max="99"
                  value={valorGlobal}
                  onChange={(e) => setValorGlobal(e.target.value)}
                  placeholder="Nível..."
                  className="text-xs border border-zinc-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none w-20"
                />
              )}
              {campoGlobal === 'data_entrada_funcao_publica' && (
                <input
                  type="date"
                  value={valorGlobal}
                  onChange={(e) => setValorGlobal(e.target.value)}
                  className="text-xs border border-zinc-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none"
                />
              )}
              {campoGlobal === 'unidade_organica' && (
                <select
                  value={valorGlobal}
                  onChange={(e) => setValorGlobal(e.target.value)}
                  className="text-xs border border-zinc-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none cursor-pointer"
                >
                  <option value="">Escolher unidade...</option>
                  {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              )}

              <button
                onClick={aplicarGlobal}
                disabled={!valorGlobal}
                className="whitespace-nowrap text-xs px-3 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-700 disabled:opacity-40 transition-colors cursor-pointer"
              >
                Aplicar a todos
              </button>
              <span className="text-xs text-zinc-400 ml-auto">{modificadosCount} alteração(ões) pendente(s)</span>
            </div>

            {/* Tabela de edição */}
            <div className="flex-1 overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white border-b border-zinc-100 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left w-10">
                      <input type="checkbox" checked={rows.every((r) => r.selecionado)} onChange={toggleTodos} className="w-4 h-4 rounded accent-zinc-800 cursor-pointer" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wide">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wide">Carreira</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wide w-20">Nível</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wide">Data Entrada F.P.</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wide">Unidade Orgânica</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className={`border-b border-zinc-50 transition-colors ${row.selecionado ? 'bg-white' : 'bg-zinc-50 opacity-50'} ${row.modificado ? 'ring-1 ring-inset ring-amber-200' : ''}`}>
                      <td className="px-4 py-2.5">
                        <input type="checkbox" checked={row.selecionado} onChange={() => toggleSelecionado(row.id)} className="w-4 h-4 rounded accent-zinc-800 cursor-pointer" />
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-sm font-medium text-zinc-800">{row.nome}</span>
                        {row.modificado && <span className="ml-2 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">editado</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <select
                          value={row.carreira}
                          onChange={(e) => updateRow(row.id, 'carreira', e.target.value)}
                          disabled={!row.selecionado}
                          className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 w-full cursor-pointer disabled:opacity-50"
                        >
                          {CARREIRAS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          type="number" min="1" max="99"
                          value={row.nivel_atual}
                          onChange={(e) => updateRow(row.id, 'nivel_atual', Number(e.target.value))}
                          disabled={!row.selecionado}
                          className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 w-16 disabled:opacity-50"
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          type="date"
                          value={row.data_entrada}
                          onChange={(e) => updateRow(row.id, 'data_entrada', e.target.value)}
                          disabled={!row.selecionado}
                          className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 disabled:opacity-50"
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <select
                          value={row.unidade_organica}
                          onChange={(e) => updateRow(row.id, 'unidade_organica', e.target.value)}
                          disabled={!row.selecionado}
                          className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 w-full cursor-pointer disabled:opacity-50"
                        >
                          <option value="">-- Sem unidade --</option>
                          {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-between gap-4">
              {error && (
                <p className="text-xs text-red-600 flex items-center gap-1.5">
                  <i className="ri-error-warning-line" />
                  {error}
                </p>
              )}
              {!error && <div />}
              <div className="flex items-center gap-3">
                <button onClick={() => setStep('filtro')} className="whitespace-nowrap px-4 py-2.5 text-sm text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer">
                  Voltar
                </button>
                <button
                  onClick={guardar}
                  disabled={saving || modificadosCount === 0}
                  className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 transition-colors cursor-pointer"
                >
                  {saving && <i className="ri-loader-4-line animate-spin text-sm" />}
                  Guardar {modificadosCount > 0 ? `${modificadosCount} alteração(ões)` : ''}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
